import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { umbracoApi } from '../services/umbracoApi';
import { featuresApi, TenantFeatures } from '../services/featuresApi';
import { config, mediaUrl } from '../config';

function loadStylesheet(href: string): Promise<void> {
  const bustCache = (url: string) => {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${process.env.REACT_APP_BUILD_TS || Date.now()}`;
  };
  const cachedHref = bustCache(href);

  return new Promise((resolve, reject) => {
    // Check if already loaded (match without query string)
    const existing = document.querySelector(`link[href^="${href}"]`);
    if (existing) {
      resolve();
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cachedHref;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
    document.head.appendChild(link);

    // Timeout after 5 seconds to avoid indefinite loading
    setTimeout(() => resolve(), 5000);
  });
}

interface NavigationItem {
  label: string;
  url: string;
  openInNewTab: boolean;
}

interface SiteSettings {
  siteNodeId: string | null;
  siteName: string;
  logo: { url: string } | null;
  customStylesheet: { url: string } | null;
  footerText: string;
  address: string;
  phone: string;
  email: string;
  facebookUrl: string;
  instagramUrl: string;
  navigation: NavigationItem[];
  showReserveButton: boolean;
  reserveButtonText: string;
  showUserAvatar: boolean;
  phoneButtonNumber: string;
  seoDescription: string;
  ogImage: { url: string } | null;
  businessType: string;
  features: TenantFeatures;
}

interface SiteSettingsContextType {
  settings: SiteSettings;
  isLoading: boolean;
  error: string | null;
}

const defaultSettings: SiteSettings = {
  siteNodeId: null,
  siteName: config.siteName,
  logo: null,
  customStylesheet: null,
  footerText: '',
  address: '',
  phone: '',
  email: '',
  facebookUrl: '',
  instagramUrl: '',
  navigation: [],
  showReserveButton: true,
  reserveButtonText: '',
  showUserAvatar: true,
  phoneButtonNumber: '',
  seoDescription: '',
  ogImage: null,
  businessType: '',
  features: {
    groupClassesEnabled: false,
    smsNotificationsEnabled: false,
    passesEnabled: false,
    groupClassCalendarViewEnabled: false,
    paymentCollectionEnabled: false,
    guestReservationsEnabled: false,
  },
};

const SiteSettingsContext = createContext<SiteSettingsContextType>({
  settings: defaultSettings,
  isLoading: true,
  error: null,
});

export const SiteSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Kick /settings/ and /frontPage off in parallel — they're
        // independent requests and both are on the critical path for the
        // loader to disappear. umbracoApi dedupes so HomePage's later call
        // reuses the same frontPage response.
        const [response, frontPage] = await Promise.all([
          umbracoApi.getContentByPath('/settings/'),
          umbracoApi.getFrontPage(),
        ]);
        const props = response.properties;
        const siteNodeId = frontPage.id;

        // Load tenant features (depends on siteNodeId)
        let features = defaultSettings.features;
        if (siteNodeId) {
          try {
            features = await featuresApi.getFeatures(siteNodeId);
          } catch (err) {
            console.warn('Failed to load features, using defaults:', err);
          }
        }

        setSettings({
          siteNodeId,
          siteName: props.siteName || defaultSettings.siteName,
          logo: props.logo?.[0] ? { url: mediaUrl(props.logo[0].url) || props.logo[0].url } : null,
          customStylesheet: props.customStylesheet?.[0] ? { url: mediaUrl(props.customStylesheet[0].url) || props.customStylesheet[0].url } : null,
          footerText: props.footerText || '',
          address: props.address || '',
          phone: props.phone || '',
          email: props.email || '',
          facebookUrl: props.facebookUrl || '',
          instagramUrl: props.instagramUrl || '',
          navigation: props.navigationItems?.items?.map((item: any) => ({
            label: item.content?.properties?.label || '',
            url: item.content?.properties?.url || '',
            openInNewTab: item.content?.properties?.openInNewTab || false,
          })) || [],
          showReserveButton: props.showReserveButton ?? true,
          reserveButtonText: props.reserveButtonText || '',
          showUserAvatar: props.showUserAvatar ?? true,
          phoneButtonNumber: props.phoneButtonNumber || '',
          seoDescription: props.seoDescription || '',
          ogImage: props.ogImage?.[0] ? { url: mediaUrl(props.ogImage[0].url) || props.ogImage[0].url } : null,
          businessType: Array.isArray(props.businessType) ? (props.businessType[0] || '') : (props.businessType || ''),
          features,
        });

        // Load custom stylesheet if provided, and wait for it to load
        const themeUrl = props.customStylesheet?.[0]?.url
          ? (mediaUrl(props.customStylesheet[0].url) || props.customStylesheet[0].url)
          : config.theme || null;

        if (themeUrl) {
          await loadStylesheet(themeUrl);
        }
      } catch (err) {
        console.error('Failed to load site settings:', err);
        // Still load the theme from config even if settings fail
        if (config.theme) {
          await loadStylesheet(config.theme);
        }
        setError('Failed to load site settings');
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ settings, isLoading, error }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => {
  return useContext(SiteSettingsContext);
};
